import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId as scrollTo } from "@/hooks/useLenis";

const HERO_BG =
  "https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGx1eHVyeSUyMGRhcmt8ZW58MHx8fHwxNzgyNDk4MDU1fDA&ixlib=rb-4.1.0&q=85";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.3 + i * 0.12, ease: [0.76, 0, 0.24, 1] },
  }),
};

const Line = ({ children, i, className = "" }) => (
  <span className="reveal-line">
    <motion.span
      custom={i}
      variants={lineVariants}
      initial="hidden"
      animate="show"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const stats = [
    { v: t.hero.stat1, l: t.hero.stat1l },
    { v: t.hero.stat2, l: t.hero.stat2l },
    { v: t.hero.stat3, l: t.hero.stat3l },
  ];

  return (
    <section id="hero" ref={ref} className="relative min-h-screen w-full overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/60 via-[#0A0A0B]/75 to-[#0A0A0B]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-center pt-28 pb-16"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-gold" />
          <span className="flo-label">{t.hero.kicker}</span>
        </motion.div>

        <h1 className="font-serif-display text-white leading-[0.95] tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] max-w-5xl">
          <Line i={0}>{t.hero.line1}</Line>
          <Line i={1}>{t.hero.line2}</Line>
          <Line i={2} className="italic text-gold">{t.hero.line3}</Line>
          <Line i={3}>{t.hero.line4}</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 max-w-xl text-zinc-400 text-base md:text-lg leading-relaxed"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo("contact")}
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 bg-gold text-[#0A0A0B] font-medium rounded-full pl-6 pr-5 py-3.5 hover:bg-[#d8b877] transition-colors duration-300"
          >
            {t.hero.ctaPrimary}
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </button>
          <button
            onClick={() => scrollTo("approach")}
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 text-white border border-white/20 rounded-full px-6 py-3.5 hover:border-gold hover:text-gold transition-colors duration-300"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="mt-16 grid grid-cols-3 gap-6 max-w-2xl border-t border-white/10 pt-8"
        >
          {stats.map((s) => (
            <div key={s.l} data-testid="hero-stat">
              <div className="font-serif-display text-3xl md:text-4xl text-white">{s.v}</div>
              <div className="flo-label mt-2 !text-[0.6rem] leading-tight">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-zinc-500"
      >
        <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
